import { useMemo, useState } from "react";
import { Loader2, Search, Eye, RefreshCw, Download, CalendarDays, X } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export const money = (amount, currency = "PHP") => {
  const n = Number(amount || 0);
  const formatted = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency === "PHP" ? `\u20b1${formatted}` : `${formatted} ${currency}`;
};

export const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export const humanize = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());

// Client-side CSV export. Builds a CSV from the given rows (defaults to every
// field on the record) and triggers a browser download. Used by the external
// modules whose data lives client-side (live API response or mock fallback).
export const downloadCsv = (filename, rows, keys) => {
  if (!rows || !rows.length) return;
  const cols = keys && keys.length ? keys : Object.keys(rows[0]);
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = cols.join(",");
  const body = rows.map((r) => cols.map((c) => esc(r[c])).join(",")).join("\n");
  const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const PageHeader = ({ title, subtitle, actions, mocked }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-bold tracking-tight text-halalgo-ink" data-testid="admin-page-title">{title}</h1>
        {mocked === true ? <SampleBadge /> : mocked === false ? <LiveBadge /> : null}
      </div>
      {subtitle ? <p className="mt-1 text-sm text-halalgo-ink/60">{subtitle}</p> : null}
    </div>
    {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
  </div>
);

export const SampleBadge = () => (
  <span
    data-testid="sample-data-badge"
    title="Live API unreachable - showing sample data"
    className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-700"
  >
    Sample data
  </span>
);

export const LiveBadge = () => (
  <span
    data-testid="live-data-badge"
    title="Connected to the live API"
    className="inline-flex items-center gap-1.5 rounded-md border border-halalgo-green-200 bg-halalgo-green-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-halalgo-green-800"
  >
    <span className="relative flex h-2 w-2">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-halalgo-green-600 opacity-75" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-halalgo-green-600" />
    </span>
    Live
  </span>
);

const STATUS_COLORS = {
  active: "green", approved: "green", paid: "green", delivered: "green", resolved: "green", online: "green",
  pending: "amber", pending_review: "amber", pending_approval: "amber", unpaid: "amber", processing: "amber", preparing: "amber",
  out_for_delivery: "blue", refunded: "blue",
  suspended: "red", cancelled: "red", rejected: "red",
  offline: "gray",
};

const COLOR_CLASSES = {
  green: "border-halalgo-green-200 bg-halalgo-green-50 text-halalgo-green-800",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  red: "border-red-200 bg-red-50 text-red-700",
  blue: "border-sky-200 bg-sky-50 text-sky-700",
  gray: "border-halalgo-kraft-200 bg-halalgo-kraft-50 text-halalgo-kraft-800",
};

export const StatusBadge = ({ value }) => {
  const color = STATUS_COLORS[value] || "gray";
  return (
    <span className={cn("inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium", COLOR_CLASSES[color])}>
      {humanize(value)}
    </span>
  );
};

export const StatCard = ({ icon: Icon, label, value, hint, accent = "green" }) => {
  const ring = accent === "amber" ? "bg-amber-50 text-amber-600" : accent === "red" ? "bg-red-50 text-red-600" : accent === "kraft" ? "bg-halalgo-kraft-50 text-halalgo-kraft-700" : "bg-halalgo-green-50 text-halalgo-green-700";
  return (
    <div className="rounded-xl border border-halalgo-kraft-100 bg-white p-5 shadow-lift-sm" data-testid="admin-stat-card">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-halalgo-ink/55">{label}</p>
        {Icon ? <span className={cn("flex h-8 w-8 items-center justify-center rounded-lg", ring)}><Icon className="h-4 w-4" /></span> : null}
      </div>
      <p className="mt-3 text-2xl font-bold text-halalgo-ink">{value}</p>
      {hint ? <p className="mt-1 text-xs text-halalgo-ink/50">{hint}</p> : null}
    </div>
  );
};

export const Loading = ({ label = "Loading..." }) => (
  <div className="flex items-center justify-center gap-2 py-16 text-halalgo-ink/50" data-testid="admin-loading">
    <Loader2 className="h-5 w-5 animate-spin" /> {label}
  </div>
);

// Generic table. columns: [{ key, label, render?(row) }]
export const SimpleTable = ({ columns, rows, empty = "No records found.", testId, onRowClick }) => (
  <div className="overflow-x-auto rounded-xl border border-halalgo-kraft-100 bg-white shadow-lift-sm">
    <Table data-testid={testId}>
      <TableHeader>
        <TableRow className="bg-halalgo-paper2 hover:bg-halalgo-paper2">
          {columns.map((c) => (
            <TableHead key={c.key} className="whitespace-nowrap text-xs uppercase tracking-wide text-halalgo-ink/55">
              {c.label}
            </TableHead>
          ))}
          {onRowClick ? <TableHead className="w-10" /> : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length + (onRowClick ? 1 : 0)} className="py-12 text-center text-halalgo-ink/50" data-testid="admin-empty-state">
              {empty}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((r, i) => (
            <TableRow
              key={r.id || i}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              data-testid={testId ? `${testId}-row` : undefined}
              className={cn("hover:bg-halalgo-green-50/40", onRowClick && "cursor-pointer")}
            >
              {columns.map((c) => (
                <TableCell key={c.key} className="align-top text-halalgo-ink/80">
                  {c.render ? c.render(r) : r[c.key] ?? "-"}
                </TableCell>
              ))}
              {onRowClick ? (
                <TableCell className="align-top text-halalgo-ink/40">
                  <Eye className="h-4 w-4" />
                </TableCell>
              ) : null}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </div>
);

// Toolbar with a search input + optional status filter + right-aligned slot.
export const Toolbar = ({ query, onQuery, placeholder = "Search...", filter, filterValue, onFilter, extra, right }) => (
  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
      <div className="relative w-full sm:max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-halalgo-ink/40" />
        <Input
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder={placeholder}
          className="h-10 pl-9"
          data-testid="admin-search-input"
        />
      </div>
      {filter ? (
        <Select value={filterValue} onValueChange={onFilter}>
          <SelectTrigger className="h-10 w-full sm:w-52" data-testid="admin-filter-select">
            <SelectValue placeholder={filter.placeholder || "Filter"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{filter.allLabel || "All"}</SelectItem>
            {filter.options.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
      {extra}
    </div>
    {right ? <div className="flex flex-wrap items-center gap-3">{right}</div> : null}
  </div>
);

// A compact date-range control (From / To) used to narrow rows before export.
// Value/onChange work on { from, to } as yyyy-mm-dd strings ("" = unset).
export const DateRangePicker = ({ value, onChange, label = "Date range" }) => {
  const { from, to } = value || {};
  const active = !!(from || to);
  const summary = active
    ? `${from ? new Date(`${from}T00:00:00`).toLocaleDateString() : "\u2026"} \u2192 ${to ? new Date(`${to}T00:00:00`).toLocaleDateString() : "\u2026"}`
    : label;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-10 w-full justify-start gap-2 border-halalgo-kraft-200 font-normal sm:w-auto",
            active ? "text-halalgo-ink" : "text-halalgo-ink/55"
          )}
          data-testid="admin-daterange-trigger"
        >
          <CalendarDays className="h-4 w-4" />
          <span className="truncate">{summary}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3" align="start" data-testid="admin-daterange-popover">
        <div className="space-y-1.5">
          <Label htmlFor="daterange-from" className="text-xs font-semibold uppercase tracking-wide text-halalgo-ink/55">From</Label>
          <Input
            id="daterange-from"
            type="date"
            value={from || ""}
            max={to || undefined}
            onChange={(e) => onChange({ from: e.target.value, to })}
            data-testid="admin-daterange-from"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="daterange-to" className="text-xs font-semibold uppercase tracking-wide text-halalgo-ink/55">To</Label>
          <Input
            id="daterange-to"
            type="date"
            value={to || ""}
            min={from || undefined}
            onChange={(e) => onChange({ from, to: e.target.value })}
            data-testid="admin-daterange-to"
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-halalgo-ink/60 hover:text-halalgo-ink"
          onClick={() => onChange({ from: "", to: "" })}
          disabled={!active}
          data-testid="admin-daterange-clear"
        >
          <X className="h-4 w-4" /> Clear dates
        </Button>
      </PopoverContent>
    </Popover>
  );
};

// Row detail modal — shows a formatted key/value grid plus the raw JSON record.
export const RecordDetailDialog = ({ open, onOpenChange, title, subtitle, record }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-xl" data-testid="admin-detail-dialog">
      <DialogHeader>
        <DialogTitle data-testid="admin-detail-title">{title || "Record"}</DialogTitle>
        <DialogDescription>{subtitle || "Full record details"}</DialogDescription>
      </DialogHeader>
      {record ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-halalgo-kraft-100 bg-halalgo-kraft-100 sm:grid-cols-2">
            {Object.entries(record).map(([k, v]) => (
              <div key={k} className="bg-white px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-halalgo-ink/45">{humanize(k)}</p>
                <p className="mt-0.5 break-words text-sm text-halalgo-ink/90">
                  {v === null || v === undefined || v === "" ? "-" : String(v)}
                </p>
              </div>
            ))}
          </div>
          <details className="rounded-lg border border-halalgo-kraft-100 bg-halalgo-paper2/60">
            <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold uppercase tracking-wide text-halalgo-ink/55">
              Raw JSON
            </summary>
            <pre className="max-h-64 overflow-auto border-t border-halalgo-kraft-100 bg-white px-3 py-2 text-xs leading-relaxed text-halalgo-ink/80" data-testid="admin-detail-json">
              {JSON.stringify(record, null, 2)}
            </pre>
          </details>
        </div>
      ) : null}
    </DialogContent>
  </Dialog>
);

// The single reusable data module: search + status filter + table + row detail.
// This is the shared template used by Leads (per tab) and every external module.
export const ResourceView = ({
  columns,
  rows,
  loading = false,
  searchKeys = [],
  searchPlaceholder = "Search...",
  filter,
  dateFilter,
  testId,
  detailTitle = (r) => r?.id || "Record",
  detailSubtitle,
  right,
  empty,
  exportName,
  onRefresh,
  refreshing = false,
}) => {
  const [query, setQuery] = useState("");
  const [filterValue, setFilterValue] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => {
    let out = Array.isArray(rows) ? rows : [];
    const q = query.trim().toLowerCase();
    if (q) {
      const keys = searchKeys.length ? searchKeys : out[0] ? Object.keys(out[0]) : [];
      out = out.filter((r) => keys.some((k) => String(r[k] ?? "").toLowerCase().includes(q)));
    }
    if (filter && filterValue !== "all") {
      out = out.filter((r) => String(r[filter.key] ?? "") === filterValue);
    }
    if (dateFilter && (range.from || range.to)) {
      const fromTs = range.from ? new Date(`${range.from}T00:00:00`).getTime() : null;
      const toTs = range.to ? new Date(`${range.to}T23:59:59.999`).getTime() : null;
      out = out.filter((r) => {
        const ts = new Date(r[dateFilter.key]).getTime();
        if (Number.isNaN(ts)) return false;
        if (fromTs !== null && ts < fromTs) return false;
        if (toTs !== null && ts > toTs) return false;
        return true;
      });
    }
    return out;
  }, [rows, query, filterValue, filter, searchKeys, dateFilter, range]);

  if (loading) return <Loading />;

  return (
    <div>
      <Toolbar
        query={query}
        onQuery={setQuery}
        placeholder={searchPlaceholder}
        filter={filter}
        filterValue={filterValue}
        onFilter={setFilterValue}
        extra={dateFilter ? <DateRangePicker value={range} onChange={setRange} label={dateFilter.label} /> : null}
        right={
          <>
            <span className="whitespace-nowrap text-xs text-halalgo-ink/50" data-testid="admin-result-count">
              {filtered.length} of {(rows || []).length}
            </span>
            {exportName ? (
              <Button
                variant="outline"
                size="sm"
                className="border-halalgo-kraft-200"
                onClick={() => downloadCsv(`halalgo_${exportName}.csv`, filtered)}
                disabled={!filtered.length}
                data-testid={`export-${exportName}-button`}
              >
                <Download className="h-4 w-4" /> Export CSV
              </Button>
            ) : null}
            {onRefresh ? (
              <Button
                variant="outline"
                size="sm"
                className="border-halalgo-kraft-200"
                onClick={onRefresh}
                disabled={refreshing}
                data-testid="admin-refresh-button"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
              </Button>
            ) : null}
            {right}
          </>
        }
      />
      <SimpleTable
        columns={columns}
        rows={filtered}
        testId={testId}
        onRowClick={(r) => setSelected(r)}
        empty={empty || (query || filterValue !== "all" || range.from || range.to ? "No records match your filters." : "No records found.")}
      />
      <RecordDetailDialog
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        title={selected ? detailTitle(selected) : ""}
        subtitle={selected && detailSubtitle ? detailSubtitle(selected) : undefined}
        record={selected}
      />
    </div>
  );
};

export { RefreshCw };
